import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { QuestionModel } from 'src/app/api/models';
import { ExamsService, QuestionsService } from 'src/app/api/services';
import {
  CommentListDialogComponent,
  CommentListDialogData,
} from 'src/app/cms/list/comment-list-dialog.component';
import { PanelService } from '../panel.service';
import { Panel, PanelEntityType } from '../panel.types';
import { TranslateService } from '@ngx-translate/core';
import { questionTypeDisplayFn } from 'src/app/app.types';
import { QuestionType } from 'src/app/app-constants';
import { ImportEntity, WizardImports } from '../../list/list.types';
import {
  ImportAiQuestionComponent,
  ImportQuestionType,
} from '../../common/dialogs/import-ai-question/import-ai-question.component';
import { validateQuestions } from 'src/app/utility/app.utils';
import { of } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AmplitudeSource,
  UIEventEnum,
} from 'src/app/utility/amplitude-tracking/tracking.types';
import { TrackingService } from 'src/app/utility/amplitude-tracking/tracking.service';
import _ from 'lodash';
import { Router } from '@angular/router';

/* TODO: 
  It used in books module which has old import options design.
  so this imports will be removed in future.
*/
export const getExamQuestionsImports = (
  translator: TranslateService,
  panelService: PanelService,
  matDialog: MatDialog,
  panel: Panel<QuestionModel>,
  courseContext = false,
  examId?: number,
  allowAiForSingleQuestionGeneration = false,
  trackingService?: TrackingService
): ImportEntity<QuestionModel>[] =>
  [
    allowAiForSingleQuestionGeneration
      ? {
          id: 'chat-gpt',
          title: translator.instant(
            'content.question.description.aiImportTitle'
          ),
          description: translator.instant(
            'content.question.description.aiImportDescription'
          ),
          highlight: true,
          action: {
            icon: 'code',
            click: () => {
              matDialog
                .open<ImportAiQuestionComponent, ImportQuestionType>(
                  ImportAiQuestionComponent,
                  {
                    data: {
                      type: 'exam-question',
                    },
                    autoFocus: false,
                    width: '600px',
                    maxHeight: '90vh',
                  }
                )
                .afterClosed()
                .subscribe((r) => {
                  if (r) {
                    matDialog.closeAll();
                    panelService.importChatGPT$.next(true);
                  }
                });
            },
            text: translator.instant(
              'content.question.addMenu.generateQuestions'
            ),
          },
        }
      : null,
    {
      title: translator.instant(
        'content.examQuestion.title.generateQuestionsWithChatGPT'
      ),
      description: translator.instant(
        'content.question.description.generateQuestionWithChatGPT'
      ),
      action: {
        icon: 'code',
        click: () => {
          if (courseContext) {
            panelService.importFromChatGPT(panel);
          } else {
            const callback = () => {
              const queryParams = {
                type: PanelEntityType.ExamQuestion,
              };
              panelService.redirectToImportItems(
                `/cms/csv-imports/question/${examId}`,
                queryParams,
                true
              );
            };
            panelService.importFromChatGPT(panel, () => callback());
          }
        },
        text: translator.instant('content.question.addMenu.generateQuestions'),
      },
    },
    {
      title: translator.instant(
        'content.question.title.importExistingQuestions'
      ),
      description: translator.instant(
        'content.question.description.importExistingQuestions'
      ),
      action: {
        icon: 'move_to_inbox',
        click: () => {
          if (!courseContext && examId) {
            const queryParams = {
              parentId: examId,
              type: PanelEntityType.ExamQuestion,
            };
            panelService.redirectToImportItems(
              `/cms/import-items`,
              queryParams,
              true
            );
          } else {
            panelService.importItems(panel);
          }
        },
        text: translator.instant('content.question.addMenu.existingQuestions'),
      },
    },
    {
      title: translator.instant('content.question.title.addPollQuestionTitle'),
      description: translator.instant(
        'content.question.description.addPollQuestionTitle'
      ),
      action: {
        icon: 'description',
        click: () => {
          if (!courseContext && examId) {
            trackingService.trackEvent({
              event_type: UIEventEnum.CSV_IMPORT_OPENED,
              event_properties: {
                source: AmplitudeSource.EXAM,
              },
            });
            const queryParams = {
              type: PanelEntityType.ExamQuestion,
            };
            panelService.redirectToImportItems(
              `/cms/csv-imports/question/${examId}`,
              queryParams,
              true
            );
          } else {
            panelService.importItemAsCSV(panel, AmplitudeSource.EXAM);
          }
        },
        text: translator.instant(
          'content.question.addMenu.questionsFromExcelCSV'
        ),
      },
      extra: {
        name: translator.instant('content.question.title.downloadCSV'),
        type: 'link' as 'link',
        icon: 'download',
        click: () => {
          window.open(
            'assets/import-csv-templates/Sample-import-questions.csv',
            '_blank'
          );
        },
      },
    },
    {
      id: 'import-json',
      title: translator.instant(
        'content.question.title.importQuestionFromJSON'
      ),
      description: translator.instant(
        'content.question.description.importQuestionFromJSON'
      ),
      action: {
        icon: 'code',
        click: () => panelService.importItemAsJSON(panel),
        text: translator.instant('content.question.addMenu.questionsFromJSON'),
      },
    },
  ].filter((n) => n);

export const getWizardExamQuestionsImports = (
  translator: TranslateService,
  panelService: PanelService,
  matDialog: MatDialog,
  panel: Panel<QuestionModel>,
  courseContext = false,
  queryParams?: { [key: string]: any },
  allowAiForSingleQuestionGeneration = false,
  trackingService?: TrackingService,
  router?: Router
): WizardImports => {
  return {
    createWithAI: {
      text: translator.instant('content.wizardActions.title.createWithAI'),
      icon: 'auto_awesome',
      description: translator.instant(
        'content.wizardActions.description.createQuestionWithAI'
      ),
      tag: translator.instant('content.wizardActions.tag.createWithAI'),
      click: () => {
        let examId = queryParams?.exam;
        let courseId = null;
        if (router.url.includes('courseId')) {
          courseId = router.url.split('courseId=')[1].split('&')[0];
        }
        if (!examId) {
          examId = router.url.split('/exams/')[1].split('?')[0];
        }
        const callback = () => {
          const queryParams = {
            course: courseId,
            type: PanelEntityType.ExamQuestion,
          };
          panelService.redirectToImportItems(
            `/cms/csv-imports/question/${examId}`,
            queryParams,
            true
          );
        };
        panelService.importFromChatGPT(panel, () => callback());
      },
    },
    createButton: {
      text: translator.instant('content.wizardActions.title.createManually'),
      icon: 'edit',
      description: translator.instant(
        'content.wizardActions.description.createManually'
      ),
      click: () => {
        const query = _.cloneDeep(queryParams);
        if (router.url.includes('courseId')) {
          query.courseId = router.url.split('courseId=')[1].split('&')[0];
        }
        matDialog.closeAll();
        router.navigate(['/cms/add/question'], { queryParams: query });
      },
    },
    importButtons: [
      {
        text: translator.instant(
          'content.wizardActions.title.createIndividualQuestionsWithAI'
        ),
        description: translator.instant(
          'content.wizardActions.description.createIndividualQuestionsWithAI'
        ),
        id: 'create-individual-questions-with-ai',
        icon: 'auto_awesome',
        hidden: !allowAiForSingleQuestionGeneration,
        click: () => {
          matDialog
            .open<ImportAiQuestionComponent, ImportQuestionType>(
              ImportAiQuestionComponent,
              {
                data: {
                  type: 'exam-question',
                },
                autoFocus: false,
                width: '600px',
                maxHeight: '90vh',
              }
            )
            .afterClosed()
            .subscribe((r) => {
              if (r) {
                matDialog.closeAll();
                panelService.importChatGPT$.next(true);
              }
            });
        },
      },
      {
        text: translator.instant(
          'content.wizardActions.title.useExistingQuestions'
        ),
        description: translator.instant(
          'content.wizardActions.description.useExistingQuestions'
        ),
        id: 'use-existing-questions',
        icon: 'file_copy',
        click: () => {
          const examId = queryParams?.exam;
          if (!courseContext && examId) {
            const queryParams = {
              parentId: examId,
              type: PanelEntityType.ExamQuestion,
            };
            panelService.redirectToImportItems(
              `/cms/import-items`,
              queryParams,
              true
            );
          } else {
            panelService.importItems(panel);
          }
        },
      },
      {
        text: translator.instant('content.wizardActions.title.importFromJSON'),
        description: translator.instant(
          'content.wizardActions.description.importFromJSON'
        ),
        id: 'import-json',
        icon: 'save_alt',
        click: () => panelService.importItemAsJSON(panel),
      },
      {
        text: translator.instant('content.wizardActions.title.importFromCSV'),
        description: translator.instant(
          'content.wizardActions.description.importFromCSV'
        ),
        id: 'import-csv',
        icon: 'save_alt',
        click: () => {
          const examId = queryParams?.exam;
          if (!courseContext && examId) {
            trackingService.trackEvent({
              event_type: UIEventEnum.CSV_IMPORT_OPENED,
              event_properties: {
                source: AmplitudeSource.EXAM,
              },
            });
            const queryParams = {
              type: PanelEntityType.ExamQuestion,
            };
            panelService.redirectToImportItems(
              `/cms/csv-imports/question/${examId}`,
              queryParams,
              true
            );
          } else {
            panelService.importItemAsCSV(panel, AmplitudeSource.EXAM);
          }
        },
      },
    ],
  };
};
@Injectable()
export class ExamQuestionPanel extends Panel<QuestionModel> {
  constructor(
    exams: ExamsService,
    matDialog: MatDialog,
    panelService: PanelService,
    questions: QuestionsService,
    translator: TranslateService,
    matSnackBar: MatSnackBar,
    router: Router
  ) {
    super();
    super.init({
      addMenuItems: [
        {
          icon: 'add',
          onClick: () => panelService.editItem(this),
          text: translator.instant(
            'content.examQuestion.addMenu.createNewQuizQuestion'
          ),
        },
      ],
      showChatGptAction: true,
      wizardImports: getWizardExamQuestionsImports(
        translator,
        panelService,
        matDialog,
        this,
        true,
        null,
        true,
        null,
        router
      ),
      imports: getExamQuestionsImports(
        translator,
        panelService,
        matDialog,
        this,
        true,
        null,
        true
      ),
      itemSuffixes: (question) => {
        const type = questionTypeDisplayFn(question.type, true) ?? '?';
        const suffixes =
          question.type === QuestionType.FreeText
            ? [{ text: '', icon: 'notes' }]
            : [{ text: type, icon: null }];
        return suffixes;
      },
      changeItemPosition: (questionId, examId, position) => {
        return exams.repositionQuestionInExam({
          id: examId,
          question_id: questionId,
          body: { position },
        });
      },
      createItemsFn: (questions, examId) => {
        const sentryInfo = {
          title: `Error while importing questions`,
          file: 'exam-question-panel.ts - createItemsFn',
          api: 'copyQuestionToExam',
          preventLogs: true,
        };
        const { isValid, error } = validateQuestions(
          sentryInfo,
          questions,
          translator,
          matSnackBar
        );
        if (!isValid) {
          return of({ error });
        }
        return exams.copyQuestionToExam({
          id: examId,
          body: questions,
        });
      },
      deleteItem: (questionId) =>
        questions.deleteQuestion({
          id: questionId,
        }),
      editDialogInput: (question, examId) => ({
        enableNavigation: true,
        examId,
        question,
      }),
      fetchItem: (id) => questions.getQuestionById({ id }),
      fetchItems: (examId) =>
        exams.getQuestionsForExam({
          id: examId ? examId : null,
        }),
      headerIcon: 'question_answers',
      itemActions: [
        {
          icon: 'edit',
          label: translator.instant('common.tooltip.edit'),
          onClick: (question) => panelService.editItem(this, question),
        },
        {
          icon: 'question_answer',
          label: translator.instant('content.examQuestion.actionMenu.comments'),
          onClick: (question) => {
            matDialog.open(CommentListDialogComponent, {
              data: {
                questionId: question.id,
              },
              autoFocus: false,
            } as MatDialogConfig<CommentListDialogData>);
          },
        },
        {
          icon: 'code',
          label: translator.instant(
            'content.examQuestion.actionMenu.downloadJSONs'
          ),
          onClick: (item) => panelService.exportItemAsJSON(this, item.id),
        },
        {
          icon: 'link_off',
          label: translator.instant(
            'content.examQuestion.actionMenu.removeFromExam'
          ),
          onClick: (question) => panelService.unlinkItems(this, question.id),
        },
        {
          icon: 'delete',
          label: translator.instant('content.examQuestion.actionMenu.delete'),
          onClick: (question) => panelService.deleteItem(this, question.id),
        },
      ],
      itemTextProperty: 'text',
      parentKey: 'questions',
      parentType: PanelEntityType.Exam,
      selectedItemActions: [
        {
          icon: 'link_off',
          label: translator.instant('content.examQuestion.selectedItem.label'),
          onClick: (selectedItemIds) =>
            panelService.unlinkItems(this, selectedItemIds),
        },
      ],
      translations: {
        addItem: translator.instant('content.examQuestion.tooltip'),
        deleteItemError: translator.instant(
          'content.examQuestion.deleteItem.error'
        ),
        deleteItemSuccess: translator.instant(
          'content.examQuestion.deleteItem.success'
        ),
        deleteItemText: translator.instant(
          'content.examQuestion.deleteItem.text'
        ),
        deleteItemTitle: translator.instant(
          'content.examQuestion.deleteItem.title'
        ),
        header: translator.instant('common.label.questions'),
        unlinkItemConfirmText: translator.instant(
          'content.examQuestion.unlinkItem.text'
        ),
        unlinkItemConfirmTitle: translator.instant(
          'content.examQuestion.unlinkItem.title'
        ),
        unlinkItemError: translator.instant(
          'content.examQuestion.unlinkItem.error'
        ),
        unlinkItemSuccess: translator.instant(
          'content.examQuestion.unlinkItem.success'
        ),
      },
      type: PanelEntityType.ExamQuestion,
      unlinkItems: (questionIds, examId) =>
        exams.removeQuestionFromExam({
          id: examId,
          question_ids: questionIds.join(','),
        }),
    });
  }
}
